import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { combineLatest, Observable, Subject } from "rxjs";
import { map, shareReplay, switchMap, takeUntil } from "rxjs/operators";

import { TasksModel } from "../tasks.model";
import { FilterModel } from "../../filter/filter.model";
import { taskIsFiltered } from "../../home/filters";
import { TaskRecordType } from "../../shared/enums";
import { ApplicationState } from "../../shared/state/types";
import { PlanningActivity, Task } from "../../shared/types";

@Component({
	selector: "ic-task-overview",
	templateUrl: "./task-overview.component.html",
	styleUrls: ["./task-overview.component.scss"],
	standalone: false,
})
export class TaskOverviewComponent implements OnInit, OnDestroy {
	private readonly tasksModel = inject(TasksModel);
	private readonly store = inject<Store<ApplicationState>>(Store);
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly filterModel = inject(FilterModel);

	private readonly destroy$ = new Subject<void>();

	loading$ = this.store.select((state) => state.ui.tasks.loading);
	// @ts-expect-error TS2564
	tasks$: Observable<{ completed: Task[]; uncompleted: Task[] }>;
	currentShift$ = this.store.select((state) => state.data.shift);
	// @ts-expect-error TS2564
	planningActivities$: Observable<PlanningActivity[]>;
	// @ts-expect-error TS2322
	residentId$: Observable<string> = this.getResidentId();
	protected filteredTaskCodes$ = this.filterModel.filters$.pipe(
		map((filters) => (filters?.caretree?.length ? filters.caretree.map((task) => task.code) : [])),
	);
	protected filteredRoleCodes$ = this.filterModel.filters$.pipe(
		map((filters) =>
			filters?.accessGroups?.length ? filters.accessGroups.map((accessGroup) => accessGroup.code) : [],
		),
	);

	getResidentId() {
		// @ts-expect-error TS2531
		return this.route.parent.parent.paramMap.pipe(
			map((paramMap) => paramMap.get("patient")),
			shareReplay({ bufferSize: 1, refCount: true }),
		);
	}

	goToDetail(task: Task) {
		// @ts-expect-error TS2531
		const { patient } = this.route.parent.parent.snapshot.params;
		this.router.navigate([patient, "tasks", task.id]);
	}

	createDynamicTask() {
		// @ts-expect-error TS2531
		const { patient } = this.route.parent.parent.snapshot.params;
		this.router.navigate([patient, "task"]);
	}

	ngOnInit() {
		this.planningActivities$ = this.residentId$.pipe(
			switchMap((id) => this.tasksModel.getPlanningActivities(id)),
			takeUntil(this.destroy$),
		);

		// @ts-expect-error TS2322
		this.tasks$ = combineLatest([
			this.residentId$,
			this.currentShift$,
			this.filteredTaskCodes$,
			this.filteredRoleCodes$,
		]).pipe(
			map(([patient, shift, filteredTaskCodes, filteredRoleCodes]) => ({
				patient,
				shift,
				filteredTaskCodes,
				filteredRoleCodes,
			})),
			switchMap(({ patient, shift, filteredTaskCodes, filteredRoleCodes }) => {
				this.tasksModel.loadTasks(patient);

				return this.tasksModel.getTasks(patient).pipe(
					map((tasks) => {
						if (!tasks) {
							return null;
						}

						const result = {
							completed: [],
							uncompleted: [],
						};

						for (const task of tasks || []) {
							const enhancedTask = {
								...task,
								isFiltered:
									(filteredTaskCodes?.length || filteredRoleCodes?.length) &&
									taskIsFiltered(
										{
											code: task.code,
											recordType: task.recordType as TaskRecordType,
											// @ts-expect-error TS2322
											accessGroupCodes: task.accessGroups?.map((accessGroup) => accessGroup.code),
										},
										filteredTaskCodes,
										filteredRoleCodes,
									),
							};
							if (task.completed) {
								// @ts-expect-error TS2345
								result.completed.push(enhancedTask);
							} else {
								// @ts-expect-error TS2345
								result.uncompleted.push(enhancedTask);
							}
						}
						result.uncompleted = result.uncompleted
							// @ts-expect-error TS2339
							.sort((a) => (a.hasUserHandlePermission ? -1 : 0))
							// @ts-expect-error TS2339
							.sort((a) => (a.shift === shift.id ? 1 : -1));

						// @ts-expect-error TS2339
						result.completed = result.completed.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

						return result;
					}),
				);
			}),
			takeUntil(this.destroy$),
		);
	}

	ngOnDestroy() {
		this.destroy$.next();
	}
}
